import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import { Stack } from "@mui/material";
import FilterSectionContainer from "components/Conversations/Views/Drawer/FilterSectionContainer";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxVirtualizedSelectMulti from "components/UI/Select/AcxVirtualizedSelectMulti";
import { customFilterSelectStyles } from "components/Conversations/Views/Drawer/shared";
import { ClassifierOutput } from "models/ClassifierModel";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import { useEffect, useMemo } from "react";
import { useRestrictedOptions } from "../BaseFilterComponents/useRestrictedOptions";

export type ExtendedMetadataGroupedList = ClassifierOutput & {
    classifierName: string;
};

const DEFAULT_ALLOWED_MODES = ["include", "exclude"] as const;
const EMPTY_VALUES: string[] = [];

const ExtendedMetadataGroupedFilterComponent = ({
    filterKey,
    store,
    classifierId,
    optionRestriction: optionRestrictionProp,
    modeRestriction,
    restrictedValues,
}: IApplicationFilterComponent) => {
    const valuesStore = useStore(ApplicationFilterValuesStore);

    // `extendedMetadataGrouped` is keyed by the bare classifier name, not by the filter key --
    // `ApplicationFiltersInstanceStore` strips the prefix the same way before indexing it. Callers
    // supply the filter key, so normalize here rather than making each one remember to.
    const metadataKey = filterKey.replace("extendedMetadata.", "");

    const groupedFilterItem = store.extendedMetadataGrouped?.[metadataKey];
    const selectedValues = groupedFilterItem?.values ?? EMPTY_VALUES;
    const containsMode = groupedFilterItem?.contains ?? true;
    const isOneOf = containsMode;
    const allowedModes = modeRestriction?.allowedModes ?? DEFAULT_ALLOWED_MODES;
    const options = valuesStore.extendedMetadataGroupedLists[metadataKey];
    const { availableOptions } = useRestrictedOptions({
        options,
        getOptionValue: (option) => option.result,
        optionRestriction: optionRestrictionProp,
        restrictedValues,
    });
    const optionsFormatted = useMemo(
        () =>
            availableOptions.map((option) => ({
                label: option.result,
                id: option.result,
            })),
        [availableOptions],
    );
    const modeOptions: Array<{
        label: string;
        value: boolean;
        isDisabled?: boolean;
    }> = [
        {
            label: "is one of",
            value: true,
            isDisabled: !allowedModes.includes("include"),
        },
        {
            label: "is not one of",
            value: false,
            isDisabled: !allowedModes.includes("exclude"),
        },
    ];

    useEffect(() => {
        valuesStore.loadExtendedMetadataGroupedList(
            metadataKey,
            classifierId || "",
        );
    }, [classifierId, metadataKey, valuesStore]);

    useEffect(() => {
        if (!modeRestriction) return;
        const currentMode = containsMode ? "include" : "exclude";
        if (allowedModes.includes(currentMode)) return;
        store.updateExtendedMetadataGrouped(metadataKey, {
            values: selectedValues,
            contains: allowedModes[0] === "include",
        });
    }, [
        allowedModes,
        containsMode,
        metadataKey,
        modeRestriction,
        selectedValues,
        store,
    ]);

    return (
        <FilterSectionContainer>
            <Stack direction="column">
                <AcxSelectSingle
                    id={`application-filters-extended-metadata-contains-${filterKey}`}
                    options={modeOptions}
                    labelField="label"
                    valueField="value"
                    defaultValue={{
                        label: isOneOf ? "is one of" : "is not one of",
                        value: isOneOf,
                    }}
                    customStyle={customFilterSelectStyles}
                    onChange={(item) => {
                        if (item.isDisabled) return;
                        store.updateExtendedMetadataGrouped(metadataKey, {
                            values: selectedValues,
                            contains: item.value,
                        });
                    }}
                    isDisabled={modeOptions.filter((item) => !item.isDisabled).length <= 1}
                />
                <AcxVirtualizedSelectMulti
                    id={`application-filters-extended-metadata-list-${filterKey}`}
                    defaultSelectedIds={selectedValues}
                    onApply={(selectedIds: string[]) => {
                        if (!options) return; // Wait for values to load before applying
                        store.updateExtendedMetadataGrouped(metadataKey, {
                            contains: containsMode,
                            values: selectedIds,
                        });
                    }}
                    options={optionsFormatted}
                    sortOnOpenOnly
                    isLoading={valuesStore.getTaskLoading(
                        ApplicationFilterValuesStore.Tasks
                            .LOAD_EXTENDED_METADATA_GROUPED_LIST,
                    )}
                    itemName={metadataKey}
                    labelField="label"
                    idField="id"
                />
            </Stack>
        </FilterSectionContainer>
    );
};

export const ExtendedMetadataGroupedFilter = observer(
    ExtendedMetadataGroupedFilterComponent,
);
